import React, {ReactNode} from "react";
import {Box, Inline, Text, VisuallyHidden} from "@unibuddy/patron";
import {MessageBubbleDesktop, MessageRow, MessageText} from "@unibuddy/chat-ui";
import {Align, Position} from "@unibuddy/chat-ui/dist/Message/types";
import {format} from "date-fns";
import LinkifyIt from "linkify-it";
import styled from "styled-components";

import {rootURL} from "shared/Utils/WhereAmI";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";

import UnfurlLink from "../UnfurlLink/UnfurlLink";
import useUniversityColor from "../../../UniversityProvider/hooks/useUniversityColor";
import {UniversityField} from "../../../../../types/generated";

const linkify = LinkifyIt();

const MessageTextWrapper = styled(Box)`
    & a {
        text-decoration: underline !important;
    }
`;

type MessageProps = {
    position: Position;
    align: Align;
    datestamp: string | null;
    children: ReactNode;
    avatar?: ReactNode | null;
    university?: UniversityField;
    isMyMessage: boolean;
};

function MessageContent({align, university, isMyMessage, children}) {
    const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", university);
    const isShowCountryFlagEnabled = isFeatureEnabled("showCountryFlag", university);
    const isBlogsEnabled = isFeatureEnabled("userGeneratedContent", university);

    // We want to check if a message contains a link before including <UnfurlLink>
    return (
        <Box
            role="document"
            // eslint-disable-next-line jsx-a11y/aria-props
            aria-roledescription="message"
        >
            <VisuallyHidden>{isMyMessage ? "You said: " : "They said: "}</VisuallyHidden>
            <MessageTextWrapper>
                <MessageText align={align}>{children}</MessageText>
            </MessageTextWrapper>
            {linkify.test(children) ? (
                <UnfurlLink
                    text={children}
                    isShowCountryFlagEnabled={isShowCountryFlagEnabled}
                    isDegreeTypesEnabled={isDegreeTypesEnabled}
                    isBlogsEnabled={isBlogsEnabled}
                    rootURL={rootURL(location.pathname)}
                />
            ) : null}
        </Box>
    );
}

/**
 * @param date eg: 2024-02-16T06:52:22.302000, 2024-02-16 06:52:22.302000 (without utc offset), 2024-02-16 06:52:22.302000Z
 */
function getLocalTime(date: string) {
    // appending Z means adding UTC offset or +00:00 timezone which is the server timezone
    return date.includes("Z") ? new Date(date) : new Date(`${date}Z`);
}

export function Message({
    position,
    align,
    datestamp,
    avatar,
    university,
    children,
    isMyMessage,
}: MessageProps) {
    const middleDot = "\u00b7";
    const formattedTime = format(getLocalTime(datestamp), "k:mm");
    const formattedDate = format(getLocalTime(datestamp), "d MMM yyyy");
    const color = useUniversityColor();

    return (
        // Parent must be a <ul> element
        <Box role="listitem">
            <MessageRow
                position={position}
                align={align}
                avatar={avatar}
                addonTop={
                    position === "top" || position === "orphan" ? (
                        <Inline verticalAlign="center" space="xxsmall">
                            <Text size="xxsmall" color={color}>
                                {formattedTime}
                            </Text>
                            <Text size="xxsmall" weight="bold" color={color}>
                                {middleDot}
                            </Text>
                            <Text size="xxsmall">{formattedDate}</Text>
                        </Inline>
                    ) : null
                }
            >
                <MessageBubbleDesktop position={position} align={align}>
                    <MessageContent align={align} university={university} isMyMessage={isMyMessage}>
                        {children}
                    </MessageContent>
                </MessageBubbleDesktop>
            </MessageRow>
        </Box>
    );
}
